import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import Icon from 'components/commons/icon/Icon';
import SymbolChart from 'components/features/trade-detail/SymbolChart';
import SymbolPositionList from 'components/features/trade-detail/SymbolPositionList';
import SymbolOrderList from 'components/features/trade-detail/SymbolOrderList';
import SymbolNews from 'components/features/news/SymbolNews';
import { productTypes, expandTypes } from 'config/consts';
import * as converter from 'components/features/utils/converter';
import * as api from 'config/api';
import * as env from 'config/environment';
import * as consts from 'config/consts';
import { formatDate, formatDateForString, formatNumber } from 'utils/formatter';
import { useModal } from 'hooks/useModal';
import { usePreviewPageGlobalUIItems } from 'components/features/home/Home';
import { ChartInfoModal } from 'components/features/modal/ChartInfoModal';
import { getSign } from 'components/features/new-report/utils/formatter';
import NumberLabel from 'components/features/utils/NumberLabel';
import { useCustomizeHeaderContext } from 'contexts/CustomizeHeaderContext';
import { useGlobalMenuContext } from 'contexts/GlobalMenuContext';

// レコード数にあわせて、アニメーション可動域を算出
const calcMaxHeight = (expandType: number): string => {
  const zero_px = '0px';
  if (expandType === expandTypes.close) return zero_px;

  const item_h =
    document.getElementById('id_symbolPositionList')?.offsetHeight ?? 0;
  return `${item_h}px`;
};

type Page =
  | {
      tag: 'Preview';
      element: React.ReactElement;
    }
  | {
      tag: 'SetRange';
    };

type ProfitAndLossPeriodProps = {
  from: string | null;
  to: string | null;
};

type filterPeriodTypeProps = 1 | 2 | 3 | 4 | 5;

export type PriceProps = {
  Day: string;
  Close: number;
};

export type SymbolProps = {
  Info: api.GetSymbolResultInfo;
  Price: api.GetSymbolResultPrice;
  Prices: Array<PriceProps>;
};

const filterPeriodList = [
  {
    type: 1,
    label: '1ヶ月',
    day: 30,
  },
  {
    type: 2,
    label: '3ヶ月',
    day: 90,
  },
  {
    type: 3,
    label: '6ヶ月',
    day: 180,
  },
  {
    type: 4,
    label: '1年',
    day: 360,
  },
  {
    type: 5,
    label: '全期間',
  },
];

const getOldestTradeTime = (orders: api.GetOrdersResultItem[]) => {
  const tradeTimeList = orders
    .map((order, i) => {
      return order.TradeTime === null
        ? null
        : { index: i, tradeTime: order.TradeTime };
    })
    .filter(x => x) as { index: number; tradeTime: string }[];

  if (tradeTimeList.length === 0) return null;

  const oldestTradeTimeIndex = tradeTimeList.reduce((prev, current) => {
    const prevTradeTime = new Date(prev.tradeTime).getTime();
    const currentTradeTime = new Date(current.tradeTime).getTime();
    return prevTradeTime < currentTradeTime ? prev : current;
  }, tradeTimeList[0]).index;

  return orders[oldestTradeTimeIndex].TradeTime as string;
};

const TradeDetailInternal = ({
  orders,
  symbol,
}: {
  orders: api.GetOrdersResultItem[];
  symbol: string;
}) => {
  const [page, setPage] = useState<Page>({ tag: 'SetRange' });
  const toPreview = useCallback(
    (element: React.ReactElement) => setPage({ tag: 'Preview', element }),
    []
  );
  const backHome = useCallback(() => setPage({ tag: 'SetRange' }), []);
  usePreviewPageGlobalUIItems(page, backHome);
  const { setWithBell } = useCustomizeHeaderContext();
  useEffect(() => {
    return setWithBell(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // MEMO: ordersAPIのレスポンスからセレクトボックスの表示期間でフィルターしたデータ
  const [periodOrders, setPeriodOrders] = useState(
    new Array<api.GetOrdersResultItem>()
  );
  // MEMO: periodOrdersから抽出した、画面表示用のデータ
  const [limitOrders, setLimitOrders] = useState(
    new Array<api.GetOrdersResultItem>()
  );
  const [totalProfitAndLoss, setTotalProfitAndLoss] = useState<number | null>(
    null
  );
  const [profitAndLossPeriod, setProfitAndLossPeriod] = useState<
    ProfitAndLossPeriodProps
  >({
    from: null,
    to: null,
  });
  const [positions, setPositions] = useState(
    new Array<api.GetPositionsResultItem>()
  );
  const [positionsLoaded, setPositionsLoaded] = useState(false);
  const [brokers, setBrokers] = useState(
    new Array<api.GetUserBrokerResultItem>()
  );
  const [symbolInfo, setSymbolInfo] = useState<SymbolProps | null>();
  const [expandType, setExpandType] = useState<number>(expandTypes.close);
  const unmounted = useRef<boolean>(false);
  const [isShowModal, { hideModal, showModal }] = useModal();

  const expandIcon =
    expandType === expandTypes.open
      ? { name: 'chevron-down transform rotate-180', label: '閉じる' }
      : { name: 'chevron-down transform rotate-0', label: '内訳を表示' };

  const toggleBreakdown = (): void => {
    setExpandType(
      expandType === expandTypes.open ? expandTypes.close : expandTypes.open
    );
  };

  const day = new Date();
  const threeMonthAgo = new Date(day.setMonth(day.getMonth() - 3)).getTime();

  const oldestTradeTime = getOldestTradeTime(orders);
  const oldestTradeTimeNum =
    orders.length === 0 || oldestTradeTime === null
      ? 0
      : new Date(oldestTradeTime).getTime();

  const initialPeriod = threeMonthAgo < oldestTradeTimeNum ? 1 : 2;
  const [selectedFilterPeriod, setSelectedFilterPeriod] = useState<
    filterPeriodTypeProps
  >(initialPeriod);
  const fromString = (result: string) => {
    return Number(result) as filterPeriodTypeProps;
  };

  const getFilterOrders = (
    orders: api.GetOrdersResultItem[]
  ): {
    periodOrders: api.GetOrdersResultItem[];
    limitOrders: api.GetOrdersResultItem[];
    tradeFrom: string;
    tradeTo: string;
  } => {
    const period = filterPeriodList.find(
      list => list.type === selectedFilterPeriod
    )?.day;

    if (period) {
      const toDay = new Date();
      const dispPeriod = new Date(
        toDay.getTime() - 1000 * 60 * 60 * 24 * period
      );

      const periodOrders = orders.filter(
        order =>
          order.TradeTime !== null && dispPeriod < new Date(order.TradeTime)
      );
      const limitOrders = periodOrders.filter((_, i) => i < 10);
      const tradeFrom = formatDate(dispPeriod, 'yyyy/MM/dd');
      const tradeTo = formatDate(toDay, 'yyyy/MM/dd');

      return {
        periodOrders,
        limitOrders,
        tradeFrom,
        tradeTo,
      };
    } else {
      const sortOders = orders.sort((first, second) => {
        if (first.TradeTime === null && second.TradeTime !== null) {
          return 1;
        } else if (first.TradeTime !== null && second.TradeTime === null) {
          return -1;
        } else {
          return 0;
        }
      });

      const limitOrders = sortOders.filter((_, i) => i < 10);

      const isNotTradeTimeNull = orders.every(
        order => order.TradeTime !== null
      );

      const tradeFrom =
        orders.length !== 0 && isNotTradeTimeNull
          ? formatDateForString(oldestTradeTime, 'yyyy/MM/dd')
          : '----/--/--';
      const tradeTo = formatDate(new Date(), 'yyyy/MM/dd');

      return {
        periodOrders: sortOders,
        limitOrders,
        tradeFrom,
        tradeTo,
      };
    }
  };

  useEffect(() => {
    return (): void => {
      // unmount
      unmounted.current = true;
    };
  }, []);

  useEffect(() => {
    if (!symbol) return;

    api.getSymbol(symbol).then(ret_ => {
      if (!unmounted.current) {
        const prices =
          ret_ !== null &&
          ret_.Prices.filter(item => item.Close !== null && item.Close !== 0);
        const ret = { ...ret_, Prices: prices } as SymbolProps;
        setSymbolInfo(ret);
      }
    });

    api.getSymbolPositions(symbol).then(ret => {
      if (!unmounted.current) {
        setPositions(ret.Items);
        setPositionsLoaded(() => true);
      }
    });

    api.getUserBroker().then(ret => {
      if (!unmounted.current) {
        setBrokers(ret.Items);
      }
    });
  }, [symbol, setPositions]);

  useEffect(() => {
    const {
      periodOrders: periodOrders_,
      limitOrders: limitOrders_,
      tradeFrom,
      tradeTo,
    } = getFilterOrders(orders);

    setPeriodOrders(periodOrders_);
    setLimitOrders(limitOrders_);

    const isProfitOrLossAllNull = periodOrders_.every(
      periodOrder => periodOrder.ProfitOrLoss === null
    );
    const totalProfitAndLoss_ = isProfitOrLossAllNull
      ? null
      : periodOrders_.reduce(
          (prev, current) => prev + (current.ProfitOrLoss ?? 0),
          0
        );
    setTotalProfitAndLoss(totalProfitAndLoss_);

    setProfitAndLossPeriod({
      from: tradeFrom,
      to: tradeTo,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orders, selectedFilterPeriod]);

  if (!symbol) return <div></div>;

  const qty = positions.reduce((prev, current) => prev + (current.Qty ?? 0), 0);

  const [avilableTotalAmount, availableTotalQty] = positions.reduce(
    ([prevAmount, prevQty], current) => [
      prevAmount +
        (current.Price !== 0 && current.Price !== null && current.Qty !== null
          ? current.Price * current.Qty
          : 0),
      prevQty + (current.Price !== 0 && current.Qty !== null ? current.Qty : 0),
    ],
    [0, 0]
  );

  const price =
    availableTotalQty === 0 || avilableTotalAmount === 0
      ? null
      : avilableTotalAmount / availableTotalQty;

  const isCurrentAmountAllNull = positions.every(
    position => position.CurrentAmount === null
  );

  const totalAmount = isCurrentAmountAllNull
    ? null
    : positions.reduce((prev, current) => {
        const currentAmount =
          current.CashMargin === consts.CashMargin.信用
            ? 0
            : current.CurrentAmount ?? 0;
        return prev + currentAmount;
      }, 0);

  const isProfitOrLossAllNull = positions.every(
    position => position.ProfitOrLoss === null
  );
  const totalProfitOrLoss = isProfitOrLossAllNull
    ? null
    : positions.reduce(
        (prev, current) => prev + (current.ProfitOrLoss ?? 0),
        0
      );

  const isAllPositionsMargin = positions.every(
    position => position.CashMargin === consts.CashMargin.信用
  );

  // max-h-custom1・max-h-0の指定を止めて、maxHeightを算出
  const maxHeight = calcMaxHeight(expandType);
  const openCloseAnimation =
    expandType === expandTypes.open
      ? 'ui-effect-fade-in-300 visible mt-1 mb-2'
      : 'ui-effect-fade-out-300 invisible';

  const updDateTime = brokers.reduce(
    (prev, current) => (prev.UpdTime < current.UpdTime ? current : prev),
    { UpdTime: '' }
  )?.UpdTime;

  const readMoreOrders = (): void => {
    setLimitOrders(periodOrders.filter((_, i) => i < limitOrders.length + 10));
  };

  const isInvestmentTrust =
    symbolInfo !== undefined &&
    symbolInfo !== null &&
    symbolInfo.Info.ProductType === productTypes.投資信託;

  const details = [
    { text: '数量', value: qty, width: 'w-2/5' },
    { text: '平均取得単価', value: price, width: 'w-3/5' },
  ].map((item, index) => {
    return (
      <div
        className={`flex items-center bg-gray-300 mx-1 px-3 py-2 rounded-tl rounded-tr rounded-br rounded-bl ${item.width}`}
        key={index}>
        <div className="">{item.text}</div>
        <div className="flex-grow text-right">
          {item.value ? item.value.toLocaleString() : '---'}
        </div>
      </div>
    );
  });

  const totalProfitAndLossSign = getSign(totalProfitAndLoss ?? 0);
  const totalProfitAndLossColor =
    totalProfitAndLossSign === 'plus'
      ? 'text-upcolor'
      : totalProfitAndLossSign === 'minus'
      ? 'text-downcolor'
      : '';

  switch (page.tag) {
    case 'Preview':
      return page.element;
    case 'SetRange':
      return (
        <>
          <div className="mx-2">
            <div className="mt-2 mr-4 flex justify-end">
              <label className="text-xs text-gray-700">
                更新日時：
                {formatDateForString(updDateTime, 'yyyy/MM/dd(ddd) HH:mm')}
              </label>
            </div>
            <div className="mt-2 ui-card">
              <div className="ui-background relative text-white">
                <div className="p-4 pb-2 border-b border-gray-300">
                  <div className="flex text-sm">
                    <div>{symbol}</div>
                    <div className="ml-2">
                      {converter.getExchangeName(symbolInfo?.Info.Exchange)}
                    </div>
                  </div>
                  <div
                    className="flex items-center text-lg font-bold"
                    style={{ minHeight: '3rem', lineHeight: '1.4rem' }}>
                    {symbolInfo?.Info.Name}
                  </div>
                </div>

                <div className="flex items-center text-sm">
                  <div className="flex flex-grow items-center border-r border-gray-300 px-4 py-3">
                    <div className="">
                      {isInvestmentTrust ? '基準価額' : '株価'}(
                      {formatDateForString(symbolInfo?.Price.Day, 'MM/dd')})
                    </div>
                    <div className="flex-grow text-right">
                      {formatNumber(symbolInfo?.Price.Close, {
                        numberOfDecimal: 1,
                        noValueText: '---',
                      })}
                      円
                    </div>
                  </div>
                  <div className="px-4 py-2">
                    <a
                      href={
                        isInvestmentTrust
                          ? `${env.minkabuInvestmentTrustFundInfoBaseURL}/${symbol}`
                          : `${env.minkabuStockInfoBaseURL}/${symbol}`
                      }
                      target="_blank"
                      rel="noopener noreferrer">
                      <span className="ml-1">銘柄情報</span>
                      <Icon name="external-link"></Icon>
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-white ui-border-b">
                <div className="m-2">
                  {!positions.length && positionsLoaded && (
                    <div className="form-error-note">
                      <div className="icon-alert"></div>
                      <div className="info-label">
                        現在この銘柄の株式を保有していません
                      </div>
                    </div>
                  )}
                  {!isAllPositionsMargin ? (
                    <div className="flex items-baseline font-bold pt-1 px-2">
                      <div className="text-lg">評価額</div>
                      <div className="flex-grow text-right text-2xl">
                        {totalAmount === null
                          ? '---'
                          : totalAmount.toLocaleString()}
                      </div>
                      <div>円</div>
                    </div>
                  ) : (
                    <></>
                  )}
                  <div className="flex pt-1 px-2">
                    <div className="">評価損益</div>
                    <div className="flex-grow flex justify-end">
                      <NumberLabel
                        value={totalProfitOrLoss}
                        unitName="円"
                        isDispalyNoValueUnit
                      />
                    </div>
                  </div>

                  <div className="flex items-center my-2 text-sm border-none">
                    {details}
                  </div>
                </div>
              </div>

              {positions?.length > 0 ? (
                <div className="">
                  <div
                    className={`mx-4 transition-all duration-300 ${openCloseAnimation}`}
                    style={{ maxHeight: `${maxHeight}` }}>
                    <SymbolPositionList symbol={symbol} positions={positions} />
                  </div>
                  <div className="cursor-pointer" onClick={toggleBreakdown}>
                    <div className="flex items-center justify-center text-gray-500 my-2">
                      <Icon
                        className="text-2xl transition duration-300"
                        name={expandIcon.name}
                      />
                      <div className="ml-1 text-sm">{expandIcon.label}</div>
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
            <div className="ui-card">
              <div className="flex p-4 border-b border-gray-300">
                <div className="text-base font-bold">取引履歴</div>
              </div>
              <div className="py-4 pb-8 px-4 flex flex-col">
                <SymbolChart
                  prices={symbolInfo?.Prices}
                  orders={orders}
                  backHome={backHome}
                  toPreview={toPreview}
                  code={symbol}
                  name={symbolInfo?.Info.Name ?? '銘柄不明'}
                />
                <div
                  className="flex flex-row text-bluegray-500 ml-auto cursor-pointer"
                  onClick={showModal}>
                  <div className="text-sm">チャートについて</div>
                  <Icon
                    name="help_outline"
                    className="ml-2 text-bluegray-400"></Icon>
                </div>
              </div>
              <div className="border-b border-gray-300"></div>
              <div className="flex flex-row items-center pt-4 px-4">
                <div
                  className={`relative flex flex-row overflow-hidden w-full items-center rounded border h-8 border-bluegray-400`}
                  style={{ width: '150px' }}>
                  <select
                    className={`w-full h-full appearance-none bg-transparent text-xs pl-3 cursor-pointer`}
                    value={selectedFilterPeriod}
                    onChange={(e): void => {
                      const result = fromString(e.currentTarget.value);
                      setSelectedFilterPeriod(result);
                    }}>
                    {filterPeriodList.map((s, i) => {
                      return (
                        <option key={i} value={s.type}>
                          {s.label}
                        </option>
                      );
                    })}
                  </select>
                  <div
                    className={
                      'absolute pointer-events-none flex items-center pr-1'
                    }
                    style={{ right: '6%' }}>
                    <i
                      className={`text-bluegray-400 cursor-pointer icon-chevron-down p-0.5 ml-auto`}
                    />
                  </div>
                </div>
                <div className="ml-auto text-sm text-bluegray-500">
                  {profitAndLossPeriod.from} ~{` `}
                  {profitAndLossPeriod.to}
                </div>
              </div>
              <div className="flex flex-row p-4 m-4 bg-bluegray-50 font-bold">
                <div className="text-lg">確定損益合計</div>
                <div className={`ml-auto text-base ${totalProfitAndLossColor}`}>
                  {totalProfitAndLoss === 0 && limitOrders.length === 0
                    ? 0
                    : totalProfitAndLoss === null
                    ? '---'
                    : formatNumber(totalProfitAndLoss, { withPlusSign: true })}
                  <span className="text-xs ml-1">円</span>
                </div>
              </div>
              <SymbolOrderList
                symbol={symbol}
                orders={limitOrders}
                readMoreOrders={readMoreOrders}
                availableReadMore={limitOrders.length < periodOrders.length}
              />
            </div>
            {symbolInfo !== null && symbolInfo !== undefined && (
              <>
                <div className="ui-card">
                  {isInvestmentTrust ? (
                    <>
                      <div className="flex p-4 border-b border-gray-300">
                        <div className="text-base font-bold">レポート</div>
                      </div>
                      <div className="p-4 border-b text-sm">
                        <a
                          href={`${env.minkabuInvestmentTrustFundInfoBaseURL}/${symbol}/monthlyreport`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex flex-row">
                          <span>月次レポート</span>
                          <div className="ml-auto text-bluegray-400">
                            <Icon name="external-link" className="ml-1"></Icon>
                          </div>
                        </a>
                      </div>
                      <div className="my-2 flex items-center justify-center text-base text-link">
                        <a
                          href={`${env.minkabuInvestmentTrustFundInfoBaseURL}/${symbol}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-2 border border-solid border-link rounded">
                          <span>みんかぶ投信へ</span>
                          <Icon name="external-link" className="ml-1"></Icon>
                        </a>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="flex p-4 border-b border-gray-300">
                        <div className="text-base font-bold">ニュース</div>
                      </div>
                      <SymbolNews symbol={symbol} />
                      <div className="my-2 flex items-center justify-center text-base text-link">
                        <a
                          href={`${env.minkabuStockInfoBaseURL}/${symbol}/news`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="px-6 py-2 border border-solid border-link rounded">
                          <span>みんかぶ　株式ニュースへ</span>
                          <Icon name="external-link" className="ml-1"></Icon>
                        </a>
                      </div>
                    </>
                  )}
                </div>
              </>
            )}
          </div>
          <ChartInfoModal
            {...{
              isShow: isShowModal,
              hide: hideModal,
            }}
          />
        </>
      );
  }
};

const TradeDetail: React.FC = () => {
  const { symbol } = useParams<{ symbol: string }>();
  const { setIsShowMenu } = useGlobalMenuContext();
  const [orders, setOrders] = useState(new Array<api.GetOrdersResultItem>());

  useEffect(() => {
    return setIsShowMenu(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!symbol) return;

    api.getSymbolOrders(symbol).then(ret => {
      setOrders(ret.Items);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <TradeDetailInternal {...{ orders, symbol }} />;
};

export default TradeDetail;
